import { apiFetch, readErrorMessage, readJson } from './client';

export const CUSTOM_APP_CATEGORIES = [
  'Utilities',
  'Productivity',
  'IT Tools',
  'Analytics',
  'Microsoft 365'
] as const;

export type CustomAppCategory = (typeof CUSTOM_APP_CATEGORIES)[number];

export interface CustomAppItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  url: string;
  allowedRoles: string[];
  allowedGroupIds: string[];
  isActive: boolean;
  sortOrder: number;
  hasIconImage: boolean;
  iconImageUrl: string | null;
}

export interface CustomAppPage {
  items: CustomAppItem[];
  total: number;
  skip: number;
  take: number;
}

export interface CustomAppCategoryCount {
  name: string;
  count: number;
}

export interface CustomAppQuery {
  q?: string;
  category?: string;
  skip?: number;
  take?: number;
}

export interface CustomAppUpsertRequest {
  slug: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  url: string;
  allowedRoles: string[];
  allowedGroupIds: string[];
  isActive: boolean;
}

function toQueryString(query: CustomAppQuery): string {
  const parts: string[] = [];
  if (query.q) parts.push(`q=${encodeURIComponent(query.q)}`);
  if (query.category) parts.push(`category=${encodeURIComponent(query.category)}`);
  if (query.skip != null) parts.push(`skip=${encodeURIComponent(String(query.skip))}`);
  if (query.take != null) parts.push(`take=${encodeURIComponent(String(query.take))}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

function asPage(raw: Partial<CustomAppPage>): CustomAppPage {
  return {
    items: raw.items ?? [],
    total: Number(raw.total ?? 0),
    skip: Number(raw.skip ?? 0),
    take: Number(raw.take ?? 0)
  };
}

export async function listCustomAppsAdmin(query: CustomAppQuery = {}): Promise<CustomAppPage> {
  const res = await apiFetch(`/api/apps/custom-apps/admin/items${toQueryString(query)}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return asPage(await readJson<Partial<CustomAppPage>>(res));
}

export async function listCustomAppsAdminCategories(): Promise<CustomAppCategoryCount[]> {
  const res = await apiFetch('/api/apps/custom-apps/admin/categories');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<CustomAppCategoryCount[]>(res);
}

export async function createCustomApp(body: CustomAppUpsertRequest): Promise<CustomAppItem> {
  const res = await apiFetch('/api/apps/custom-apps/admin/items', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<CustomAppItem>(res);
}

export async function updateCustomApp(
  id: string,
  body: CustomAppUpsertRequest
): Promise<CustomAppItem> {
  const res = await apiFetch(`/api/apps/custom-apps/admin/items/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<CustomAppItem>(res);
}

export async function uploadCustomAppIcon(id: string, file: File): Promise<CustomAppItem> {
  const body = new FormData();
  body.append('file', file);
  const res = await apiFetch(`/api/apps/custom-apps/admin/items/${encodeURIComponent(id)}/icon`, {
    method: 'POST',
    body
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<CustomAppItem>(res);
}

export async function clearCustomAppIcon(id: string): Promise<CustomAppItem> {
  const res = await apiFetch(`/api/apps/custom-apps/admin/items/${encodeURIComponent(id)}/icon`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<CustomAppItem>(res);
}

export async function deactivateCustomApp(id: string): Promise<void> {
  const res = await apiFetch(
    `/api/apps/custom-apps/admin/items/${encodeURIComponent(id)}/deactivate`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
}
